import {
  FireOutlined,
  FundOutlined,
  ShopOutlined,
  ShoppingOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Layout, Menu } from 'antd';
import PropTypes from 'prop-types';
import type { ReactNode } from 'react';
import { useContext, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';

import { AdminLogo } from 'app/components/commons/icons/AdminLogo';
import { UserContext } from 'app/context/UserContext/UserContext';

import 'styles/layout/side-menu.scss';

import { PictureComponent } from '../commons/PictureComponent/PictureComponent';

const { Sider } = Layout;

type MenuItem = {
  title: string;
  link: string;
};

type SubMenuItems = Record<string, MenuItem>;

type MenuSection = {
  title: string;
  icon: ReactNode;
  items: SubMenuItems;
};

type MenuItems = Record<string, MenuSection>;

type MenuKeys =
  'catalog' | 'ecommerce' | 'purchase' | 'merchandising' | 'community';

const menuItemsOrder: MenuKeys[] = [
  'catalog',
  'ecommerce',
  'purchase',
  'merchandising',
  'community',
];

const subMenuItemsOrder: Record<MenuKeys, string[]> = {
  catalog: [
    'hotels',
    'packs',
    'activities',
    'rooms',
    'galleries',
    'customTags',
  ],
  ecommerce: ['bills', 'sales', 'commercialGestures', 'welcomeKit'],
  purchase: ['bookings', 'giftCards'],
  merchandising: [
    'event',
    'quickFilter',
    'slides',
    'guides',
    'SEOLandings',
    'collections',
  ],
  community: ['users', 'vouchers'],
};

export const SideMenuAdmin = () => {
  const { user, isSuperAdmin } = useContext(UserContext);
  const menuItems: MenuItems = {
    catalog: {
      title: 'Catalogue',
      icon: <ShopOutlined />,
      items: {
        packs: {
          title: 'Packages',
          link: '/packs',
        },
        hotels: {
          title: 'Hotels',
          link: '/hotels',
        },
        activities: {
          title: 'Activities',
          link: '/activities',
        },
        rooms: {
          title: 'Rooms',
          link: '/rooms',
        },
        galleries: {
          title: 'Gallery',
          link: '/hotels/galleries',
        },
        customTags: {
          title: 'Custom tags',
          link: '/custom-tags',
        },
      },
    },
    ecommerce: {
      title: 'E-commerce',
      icon: <FundOutlined />,
      items: {
        bills: {
          title: 'Bills',
          link: '/bills',
        },
        welcomeKit: {
          title: 'Welcome kit',
          link: '/welcome-kit',
        },
      },
    },
    purchase: {
      title: 'Purchases',
      icon: <ShoppingOutlined />,
      items: {
        bookings: {
          title: 'Bookings',
          link: '/bookings',
        },
        giftCards: {
          title: 'Gift cards',
          link: '/gift-cards',
        },
      },
    },
    merchandising: {
      title: 'Merchandising',
      icon: <FireOutlined />,
      items: {
        event: {
          title: 'Events',
          link: '/edito-events',
        },
        slides: {
          title: 'Slider',
          link: '/slider',
        },
        guides: {
          title: 'Guides',
          link: '/guides',
        },
        SEOLandings: {
          title: 'SEO landings',
          link: '/seo-landings',
        },
        collections: {
          title: 'Collections',
          link: '/collections',
        },
      },
    },
    community: {
      title: 'Community',
      icon: <TeamOutlined />,
      items: {
        users: {
          title: 'Users',
          link: '/users',
        },
        vouchers: {
          title: 'Vouchers',
          link: '/vouchers/new',
        },
      },
    },
  };

  if (isSuperAdmin) {
    menuItems.merchandising.items = {
      quickFilter: {
        title: 'Quick Filters',
        link: '/quick-filters',
      },
      ...menuItems.merchandising.items,
    };

    menuItems.ecommerce.items = {
      ...menuItems.ecommerce.items,
      sales: {
        title: 'Sale times',
        link: '/sales',
      },
      commercialGestures: {
        title: 'Commercial Gestures',
        link: '/commercial-gestures',
      },
    };
  }

  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const rootKeys = [
    'catalog',
    'ecommerce',
    'purchase',
    'merchandising',
    'community',
  ];

  // Open only one submenu at a time
  const onOpenChange = (items: string[]) => {
    const latestOpenKey = items.find((key) => openKeys.includes(key));

    if (!rootKeys.includes(latestOpenKey ?? '')) {
      setOpenKeys(items);
    } else {
      setOpenKeys(
        latestOpenKey ? items.filter((key) => key !== latestOpenKey) : []
      );
    }
  };

  const renderItem = (key: string, { link, title }: MenuItem) => ({
    key,
    className: 'menu-item',
    label: <NavLink to={link}>{title}</NavLink>,
  });
  const renderSubMenu = (
    key: keyof typeof subMenuItemsOrder,
    {
      title,
      icon,
      items,
    }: { title: string; icon: ReactNode; items: Record<string, MenuItem> }
  ) => ({
    key,
    label: title,
    icon,
    className: 'submenu',
    children: subMenuItemsOrder[key]
      .filter((subMenuKey) => !!items[subMenuKey])
      .map((subMenuKey) => renderItem(subMenuKey, items[subMenuKey])),
  });

  return (
    <Sider className="sider">
      <div>
        <Link to="/">
          <div className="title">
            <AdminLogo />
          </div>
        </Link>
        <Menu
          openKeys={openKeys}
          onOpenChange={onOpenChange}
          theme="dark"
          className="menu"
          mode="inline"
          items={menuItemsOrder.map((key) =>
            renderSubMenu(key, menuItems[key as keyof MenuItems])
          )}
        />
      </div>
      <div className="bottom_admin">
        <PictureComponent
          className="avatar"
          style={{ borderRadius: '50%' }}
          pictureId={user?.avatarPath || 'static/default-avatar.png'}
        />
        {user?.firstName || `#${user?.id}`}
      </div>
    </Sider>
  );
};

SideMenuAdmin.propTypes = {
  user: PropTypes.shape({
    firstName: PropTypes.string,
    role: PropTypes.string.isRequired,
    avatarId: PropTypes.string,
    id: PropTypes.number.isRequired,
  }),
};
